"use client";

import type { PlayerRef } from "@remotion/player";
import { FC, RefObject, useCallback, useEffect, useState } from "react";
import { MICRO_SECONDS_IN_SECOND } from "../../utils/time";
import MomentPlayerScrubber from "../MomentPlayerScrubber";
import PlayOverlay, { type PlayOverlaySize } from "../PlayOverlay";

type ShortPlayerChromeProps = {
  playerRef: RefObject<PlayerRef | null>;
  /** Rendered short duration, in µs. */
  durationUs: number;
  fps: number;
  /** Hide the bottom-right time/duration pill (bar + disc still shown). */
  showTimePill?: boolean;
  /** Drag start/end on the scrubber, forwarded from MomentPlayerScrubber. */
  onScrubbingChange?: (scrubbing: boolean) => void;
  /** The centre disc's variant — `lg` for the full players, `md` for a
   *  compact preview where the 64px disc would swallow the frame.
   *  @default "lg" */
  playOverlaySize?: PlayOverlaySize;
};

/**
 * The Create-shorts player chrome (centered PlayOverlay disc + bottom-edge
 * MomentPlayerScrubber) laid over a controls-less NLBufferingPlayer. It
 * tracks play/pause and the frame straight off the Remotion PlayerRef, so
 * the host only hands it the ref and the short's duration. Must sit inside
 * a `group` positioned box (the reveal classes ride group-hover).
 */
const ShortPlayerChrome: FC<ShortPlayerChromeProps> = ({
  playerRef,
  durationUs,
  fps,
  showTimePill = true,
  onScrubbingChange,
  playOverlaySize = "lg",
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  // While the bar is in hand the pill is shown regardless of showTimePill:
  // the countdown is the seek's readout.
  const [isScrubbing, setIsScrubbing] = useState(false);
  const handleScrubbingChange = useCallback(
    (scrubbing: boolean) => {
      setIsScrubbing(scrubbing);
      onScrubbingChange?.(scrubbing);
    },
    [onScrubbingChange],
  );
  const [currentTimeUs, setCurrentTimeUs] = useState(0);

  const frameToUs = useCallback(
    (frame: number) => (frame / fps) * MICRO_SECONDS_IN_SECOND,
    [fps],
  );

  // The ref's target may mount after us (lazy player) — poll briefly until it
  // exists, then subscribe. `frameupdate` keeps the fill live at any rate.
  useEffect(() => {
    let cancelled = false;
    let cleanup: (() => void) | null = null;
    const attach = () => {
      if (cancelled) return;
      const player = playerRef.current;
      if (!player) {
        const id = window.setTimeout(attach, 100);
        cleanup = () => window.clearTimeout(id);
        return;
      }
      setIsPlaying(player.isPlaying());
      setCurrentTimeUs(frameToUs(player.getCurrentFrame()));
      const onPlay = () => setIsPlaying(true);
      const onPause = () => setIsPlaying(false);
      const onEnded = () => setIsPlaying(false);
      const onFrame = (e: { detail: { frame: number } }) =>
        setCurrentTimeUs(frameToUs(e.detail.frame));
      player.addEventListener("play", onPlay);
      player.addEventListener("pause", onPause);
      player.addEventListener("ended", onEnded);
      player.addEventListener("frameupdate", onFrame);
      cleanup = () => {
        player.removeEventListener("play", onPlay);
        player.removeEventListener("pause", onPause);
        player.removeEventListener("ended", onEnded);
        player.removeEventListener("frameupdate", onFrame);
      };
    };
    attach();
    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [playerRef, frameToUs]);

  const seekToUs = useCallback(
    (us: number) => {
      playerRef.current?.seekTo(
        Math.round((us / MICRO_SECONDS_IN_SECOND) * fps),
      );
    },
    [playerRef, fps],
  );

  const togglePlayback = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const player = playerRef.current;
      if (!player) return;
      if (player.isPlaying()) player.pause();
      else player.play();
    },
    [playerRef],
  );

  return (
    <>
      {/* Whole-surface click-to-play (the Timeline player's clickToPlay
          idiom), under the disc and the scrubber so those keep their own
          handlers. Sits above the TikTok overlay, which is pointer-events-none. */}
      <div
        onClick={togglePlayback}
        aria-hidden
        className="absolute inset-0 z-20 cursor-pointer"
      />
      <button
        type="button"
        onClick={togglePlayback}
        aria-label={isPlaying ? "Pause" : "Play"}
        className={`absolute left-1/2 top-1/2 z-30 -translate-x-1/2 -translate-y-1/2 rounded-full outline-none transition-opacity duration-150 focus-visible:ring-2 focus-visible:ring-white ${
          isPlaying
            ? "opacity-0 group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100"
            : "opacity-100"
        }`}
      >
        <PlayOverlay playing={isPlaying} size={playOverlaySize} />
      </button>
      <div className="absolute inset-0 z-30 pointer-events-none [&>*]:pointer-events-auto">
        <MomentPlayerScrubber
          visible
          isPlaying={isPlaying}
          rangeStartUs={0}
          rangeEndUs={Math.max(1, durationUs)}
          currentTimeUs={currentTimeUs}
          onPreviewSeek={seekToUs}
          onCommitSeek={seekToUs}
          showTimePill={showTimePill || isScrubbing}
          onScrubbingChange={handleScrubbingChange}
        />
      </div>
    </>
  );
};

export default ShortPlayerChrome;
